/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiActions, uiTypes, uiSelectors } from '@opengeoweb/store';
import { DataExplorerInfoButton } from './DataExplorerInfoButton';
import type { DataExplorerTypes } from '../../store';
import { dataExplorerActions, dataExplorerSelectors } from '../../store';

interface DataExplorerInfoButtonProps {
  id: string;
  source?: uiTypes.Source;
}

export const DataExplorerInfoButtonConnect: React.FC<
  DataExplorerInfoButtonProps
> = ({ id, source = 'app' }: DataExplorerInfoButtonProps) => {
  const dispatch = useDispatch();

  const isOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.DataExplorerInfo),
  );

  const selectedId = useSelector(
    (store: DataExplorerTypes.DataExplorerModuleState) =>
      dataExplorerSelectors.getSelectedDatasetId(store),
  );

  const toggleInfoDialog = React.useCallback((): void => {
    if (selectedId === id || !isOpenInStore) {
      dispatch(
        uiActions.setActiveMapIdForDialog({
          type: uiTypes.DialogTypes.DataExplorerInfo,
          mapId: '',
          setOpen: !isOpenInStore,
          source,
        }),
      );
    }

    dispatch(dataExplorerActions.setSelectedDatasetId({ id }));
  }, [dispatch, isOpenInStore, source, id, selectedId]);

  const onClick = useCallback((): void => {
    toggleInfoDialog();
  }, [toggleInfoDialog]);

  return (
    <DataExplorerInfoButton
      onClick={onClick}
      selected={!!isOpenInStore && selectedId === id}
    />
  );
};
